/*
 * Developed for G.J. Gardner Homes by Softeq Development Corporation
 * http://www.softeq.com
 */

import { Directive, EventEmitter, OnChanges, Output, SimpleChanges } from '@angular/core';
import { Events, GridApi, GridOptions, RowNode } from 'ag-grid-community';
import { isNil } from 'lodash';
import { GridSelectedRowChangeEvent } from '../../grid-events';
import { GRID_FEATURE, GridFeature, GridFeatureInitParams, GridFeatureReadyParams } from '../feature.interfaces';

@Directive({
  selector: 'gh-grid[data]', // tslint:disable-line:directive-selector
  providers: [{
    provide: GRID_FEATURE,
    useExisting: GridDataDirective,
    multi: true,
  }],
  // tslint:disable-next-line:use-input-property-decorator
  inputs: [
    'data',
    'selectedRow',
  ],
})
export class GridDataDirective implements OnChanges, GridFeature {
  // we have to set data only when all other features were initialized.
  // this primarily relates to option bar feature
  order = 1000;

  @Output() selectedRowChange = new EventEmitter<GridSelectedRowChangeEvent<any>>();

  private api: GridApi;
  private _data: any[];
  private _selectedRow: any;

  ngOnChanges(changes: SimpleChanges): void {
    const { data, selectedRow, selectedRowIndex } = changes;
    if (data) {
      this.setData(data.currentValue);
    }
    if (selectedRow) {
      this.setSelectedRow(selectedRow.currentValue);
    }
  }

  initGrid(params: GridFeatureInitParams): void {
    // nothing to do
  }

  enhanceGridOptions(options: GridOptions): void {
    // nothing to do

    if (this._data) {
      options.rowData = this._data;
    }
  }

  onGridReady(params: GridFeatureReadyParams): void {
    const { api } = params;

    api.addEventListener(Events.EVENT_ROW_SELECTED, this.onRowSelected.bind(this));

    this.api = api;

    this.goToSelectedRow();
  }

  onDestroy(): void {
    // nothing to do
  }

  setData(data: any[]): void {
    if (this.api) {
      this.api.setRowData(data);
    }
    this._data = data;
  }

  setSelectedRow(id?: string): void {
    if (id !== this._selectedRow) {
      this._selectedRow = id;

      this.goToSelectedRow();
    }
  }

  private goToSelectedRow(): void {
    const { api, _selectedRow: row } = this;

    if (row && api) {
      const node = api.getRowNode(row);
      if (node) {
        node.setSelected(true);
      }
    }
  }

  private onRowSelected({ node }: { node: RowNode }): void {
    if (node.isSelected()) {
      if (node.id !== this._selectedRow) {
        this.selectedRowChange.emit({ id: node.id, node, data: node.data });
      }
    } else {
      const rowModel = this.api.getModel();
      const nextNode = rowModel.getRow(node.rowIndex) || rowModel.getRow(node.rowIndex - 1);
      if (nextNode && nextNode !== node) {
        nextNode.setSelected(true);
      } else if (isNil(nextNode)) {
        this.selectedRowChange.emit({});
      }
    }
  }
}
